import { Button } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useState } from "react";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";

export type SendProposalButtonProps = {
  yardId: string;
  /** Rang 2 (travaux_n_2) works contract whose draft proposals are sent. */
  contractId: number;
  /** Draft (unsent) document versions. */
  draftCount: number;
  /** Draft (unsent) maquette versions, with or without documents. */
  draftMaquetteCount: number;
  /** Refresh the list once the proposal has been sent to rang 1. */
  onSent?: () => void;
};

/** « 2 documents et 1 maquette », « 0 document » when there is nothing. */
export function proposalContent(documents: number, maquettes: number): string {
  const documentsLabel = `${documents} document${documents > 1 ? "s" : ""}`;
  const maquettesLabel = `${maquettes} maquette${maquettes > 1 ? "s" : ""}`;
  if (maquettes === 0) return documentsLabel;
  return documents === 0
    ? maquettesLabel
    : `${documentsLabel} et ${maquettesLabel}`;
}

const transmitted = (documents: number, maquettes: number) => {
  if (documents > 0) return "transmis";
  return maquettes > 1 ? "transmises" : "transmise";
};

/**
 * "Envoyer la proposition" action for the rang 2 sas prestataires screen.
 * Sending is irreversible (rang 1 receives the documents in its sas),
 * so the action always confirms before posting.
 */
export function SendProposalButton({
  yardId,
  contractId,
  draftCount,
  draftMaquetteCount,
  onSent,
}: SendProposalButtonProps) {
  const [loading, setLoading] = useState(false);
  const total = draftCount + draftMaquetteCount;

  const handleClick = async () => {
    if (total === 0 || loading) return;
    const confirmed = await confirmDialog({
      title: "Envoyer la proposition",
      message: `${proposalContent(draftCount, draftMaquetteCount)} ${
        total > 1 ? "seront" : "sera"
      } ${transmitted(draftCount, draftMaquetteCount)} au contrat travaux rang 1. Cette action est irréversible.`,
      confirmLabel: "Envoyer",
    });
    if (!confirmed) return;

    setLoading(true);
    const { data, error } = await apiClient.POST(
      "/api/yards/{yardId}/proposals/send",
      { params: { path: { yardId } }, body: { contractId } },
    );
    setLoading(false);

    if (
      isNotNil(error) ||
      isNil(data) ||
      isNotNil(data.error) ||
      isNil(data.data)
    ) {
      toaster.create({
        type: "error",
        title: "Envoi impossible",
        description:
          error?.error ??
          "Impossible d'envoyer la proposition au contrat rang 1",
      });
      return;
    }

    toaster.create({
      type: "success",
      title: "Proposition envoyée",
      description: `${proposalContent(
        data.data.sentCount,
        data.data.sentMaquetteCount,
      )} ${transmitted(
        data.data.sentCount,
        data.data.sentMaquetteCount,
      )} au contrat travaux rang 1`,
    });
    onSent?.();
  };

  return (
    <Button
      onClick={handleClick}
      disabled={total === 0 || loading}
      className="text-sm"
    >
      {loading ? "Envoi..." : "Envoyer la proposition"}
    </Button>
  );
}
